import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Popover, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { HexColorPicker } from 'react-colorful';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import { usePreventGameKeydownListening } from '@hooks';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService from '@services/BrandingsService';
import type { IColorScheme } from '@services/BrandingsService';

import { sendBrandingMapColorsUpdatedToUnreal } from '../brandingSpotsUnreal';
import { useEditorMode } from '../EditorModeContext';
import { useInspectorStyles } from './styles';

const DEFAULT_COLORS: Required<IColorScheme> = {
  primaryColor: '#000000',
  secondaryColor: '#333333',
  outlineColor: '#333333',
};

const isValidHex = (value: string) => /^#[0-9a-f]{6}$/i.test(value.trim());

const AUTOSAVE_DEBOUNCE_MS = 600;

interface ColorRowProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}

const ColorRow = ({ label, value, onChange, disabled }: ColorRowProps) => {
  const styles = useInspectorStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hexInputRef = usePreventGameKeydownListening<HTMLInputElement>();
  const normalized = isValidHex(value) ? value : '#000000';

  const handleHexChange = (raw: string) => {
    let next = raw.trim();
    if (!next.startsWith('#')) {
      next = `#${next}`;
    }
    onChange(next);
  };

  return (
    <>
      <Box sx={styles.colorRow}>
        <Box
          sx={[styles.colorSwatch, { backgroundColor: normalized }]}
          onClick={(e) => !disabled && setAnchorEl(e.currentTarget)}
        />
        <Box sx={styles.colorRowText}>
          <Typography sx={styles.colorRowLabel}>{label}</Typography>
          <Typography sx={styles.colorRowHex}>{value || normalized}</Typography>
        </Box>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: styles.pickerPopover }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <TextField
            inputRef={hexInputRef}
            size="small"
            value={value}
            onChange={(e) => handleHexChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            onKeyUp={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            sx={styles.pickerHexField}
            inputProps={{ maxLength: 7 }}
          />
          <HexColorPicker color={normalized} onChange={onChange} />
        </Box>
      </Popover>
    </>
  );
};

type AnyBranding = Record<string, unknown> & {
  brandingId?: string;
  name?: string;
  brandingColors?: IColorScheme;
};

const MapColorsInspector = () => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const { setAutosaveStatus } = useEditorMode();
  const { sendMessageToUnreal } = useMytaverse();
  const {
    currentRoom,
    currentBrandingId,
    isBrandingModeEnabled,
  } = useMytaverseEvent();

  const mapId = currentRoom?.map?.mapId;
  const activeBrandingId = currentRoom?.activeBrandingId;
  const isEditingLive =
    !!currentBrandingId && currentBrandingId === activeBrandingId;

  const [name, setName] = useState<string>('');
  const [colors, setColors] = useState<Required<IColorScheme>>(DEFAULT_COLORS);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const nameInputRef = usePreventGameKeydownListening<HTMLInputElement>();
  const nameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const colorsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadedRef = useRef(false);

  useEffect(() => {
    if (!currentBrandingId || !mapId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    initialLoadedRef.current = false;

    const load = async () => {
      try {
        const res = await BrandingsService.getBrandings({ mapId });
        if (cancelled) {
          return;
        }
        const branding = (res.brandings as AnyBranding[] | undefined)?.find(
          (b) => b?.brandingId === currentBrandingId,
        );
        const nextColors: Required<IColorScheme> = {
          primaryColor:
            branding?.brandingColors?.primaryColor ||
            DEFAULT_COLORS.primaryColor,
          secondaryColor:
            branding?.brandingColors?.secondaryColor ||
            DEFAULT_COLORS.secondaryColor,
          outlineColor:
            branding?.brandingColors?.outlineColor ||
            DEFAULT_COLORS.outlineColor,
        };
        setColors(nextColors);
        setName(typeof branding?.name === 'string' ? branding.name : '');
      } catch (loadError) {
        console.error(
          `Failed to load branding for editor: ${
            (loadError as Error)?.message
          }`,
          { brandingId: currentBrandingId },
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          initialLoadedRef.current = true;
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [currentBrandingId, mapId]);

  const persistName = useCallback(
    async (nextName: string) => {
      if (!currentBrandingId) {
        return;
      }
      setAutosaveStatus('saving');
      try {
        await BrandingsService.updateBranding(currentBrandingId, {
          name: nextName.trim() || 'Unnamed',
        });
        setAutosaveStatus('saved');
      } catch (saveError) {
        console.error(
          `Failed to save branding name: ${(saveError as Error)?.message}`,
          { brandingId: currentBrandingId },
        );
        setAutosaveStatus('error');
      }
    },
    [currentBrandingId, setAutosaveStatus],
  );

  const persistColors = useCallback(
    async (nextColors: Required<IColorScheme>) => {
      if (!currentBrandingId) {
        return;
      }
      if (
        !isValidHex(nextColors.primaryColor) ||
        !isValidHex(nextColors.secondaryColor) ||
        !isValidHex(nextColors.outlineColor)
      ) {
        setError(
          translate(
            'editorMode.invalidHex',
            'Please use HEX colors in format #RRGGBB.',
          ),
        );
        return;
      }
      setError(null);
      setAutosaveStatus('saving');
      try {
        await BrandingsService.updateBranding(currentBrandingId, {
          brandingColors: nextColors,
        });

        if (isBrandingModeEnabled) {
          sendBrandingMapColorsUpdatedToUnreal(sendMessageToUnreal, {
            brandingId: currentBrandingId,
            brandingColors: nextColors,
            roomId: currentRoom?.roomId,
            mapId: currentRoom?.map?.mapId,
          });
        }

        setAutosaveStatus('saved');
      } catch (saveError) {
        console.error(
          `Failed to save branding colors: ${(saveError as Error)?.message}`,
          { brandingId: currentBrandingId },
        );
        setAutosaveStatus('error');
      }
    },
    [
      currentBrandingId,
      currentRoom?.map?.mapId,
      currentRoom?.roomId,
      isBrandingModeEnabled,
      sendMessageToUnreal,
      setAutosaveStatus,
      translate,
    ],
  );

  const handleNameChange = (value: string) => {
    setName(value);
    if (!initialLoadedRef.current) {
      return;
    }
    if (nameDebounceRef.current) {
      clearTimeout(nameDebounceRef.current);
    }
    nameDebounceRef.current = setTimeout(() => {
      persistName(value);
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  const handleColorChange = (key: keyof IColorScheme) => (next: string) => {
    const updated = { ...colors, [key]: next };
    setColors(updated);
    if (!initialLoadedRef.current) {
      return;
    }
    if (colorsDebounceRef.current) {
      clearTimeout(colorsDebounceRef.current);
    }
    colorsDebounceRef.current = setTimeout(() => {
      persistColors(updated);
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  useEffect(() => {
    return () => {
      if (nameDebounceRef.current) {
        clearTimeout(nameDebounceRef.current);
      }
      if (colorsDebounceRef.current) {
        clearTimeout(colorsDebounceRef.current);
      }
    };
  }, []);

  const disabled = isLoading || !currentBrandingId;

  const colorFields = useMemo(
    () => [
      {
        key: 'primaryColor' as keyof IColorScheme,
        label: translate('adminToolbox.primaryColor', 'Primary Color'),
      },
      {
        key: 'secondaryColor' as keyof IColorScheme,
        label: translate('adminToolbox.secondaryColor', 'Secondary Color'),
      },
      {
        key: 'outlineColor' as keyof IColorScheme,
        label: translate('adminToolbox.outlineColor', 'Outline Color'),
      },
    ],
    [translate],
  );

  return (
    <Box>
      {isEditingLive && (
        <Box sx={styles.liveWarning}>
          <WarningAmberOutlinedIcon sx={{ fontSize: 14, color: '#FFB800' }} />
          <Typography sx={styles.liveWarningText}>
            {translate(
              'editorMode.liveBrandingWarning',
              'This configuration is live — changes apply to everyone in the room.',
            )}
          </Typography>
        </Box>
      )}

      <Box sx={styles.section}>
        <Typography sx={styles.fieldLabel}>
          {translate('editorMode.brandingName', 'Configuration name')}
        </Typography>
        <TextField
          inputRef={nameInputRef}
          fullWidth
          size="small"
          value={name}
          disabled={disabled}
          onChange={(e) => handleNameChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') {
              e.stopPropagation();
            }
          }}
          onKeyUp={(e) => {
            if (e.key !== 'Escape') {
              e.stopPropagation();
            }
          }}
          sx={styles.textField}
          inputProps={{ maxLength: 120 }}
        />
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.sectionLabel}>
          {translate('editorMode.mapColors', 'Map colors')}
        </Typography>
        {colorFields.map((field) => (
          <ColorRow
            key={field.key}
            label={field.label}
            value={colors[field.key]}
            onChange={handleColorChange(field.key)}
            disabled={disabled}
          />
        ))}
        {error && <Typography sx={styles.helperError}>{error}</Typography>}
      </Box>
    </Box>
  );
};

export default MapColorsInspector;
